import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    eyebrow?: string
    title: ReactNode
    description?: ReactNode
    align?: 'left' | 'center'
    tone?: 'dark' | 'light'
    as?: 'h1' | 'h2'
    id?: string
    className?: string
}

export const Eyebrow: FC<{ children: ReactNode, tone?: 'dark' | 'light', className?: string }> = ({ children, tone = 'dark', className }): ReactNode => {
    return (
        <p
            className={cn(
                'inline-flex items-center gap-3 text-xs font-semibold tracking-[0.2em] uppercase',
                tone === 'dark' ? 'text-timber' : 'text-forest-foreground/70',
                className
            )}
        >
            <span aria-hidden='true' className='h-px w-8 bg-current' />
            {children}
        </p>
    )
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    align = 'left',
    tone = 'dark',
    as: Tag = 'h2',
    id,
    className
}): ReactNode => {
    return (
        <div className={cn('max-w-2xl', align === 'center' && 'mx-auto text-center', className)}>
            {eyebrow && (
                <Eyebrow tone={tone} className={cn(align === 'center' && 'justify-center')}>
                    {eyebrow}
                </Eyebrow>
            )}

            <Tag
                id={id}
                className={cn(
                    'mt-4 font-heading text-[2.15rem] leading-[1.08] font-medium tracking-tight text-balance sm:text-4xl lg:text-5xl',
                    tone === 'light' && 'text-forest-foreground'
                )}
            >
                {title}
            </Tag>

            {description && (
                <p
                    className={cn(
                        'mt-5 text-lg leading-relaxed text-pretty',
                        tone === 'dark' ? 'text-muted-foreground' : 'text-forest-foreground/75'
                    )}
                >
                    {description}
                </p>
            )}
        </div>
    )
}
